import { IntlShape } from "react-intl";
import * as Yup from 'yup';
import { interventionValidationResourseUsagePartV2 } from './interventionValidationSchemaGeneralPart';
import {
  numericOptionalWithGreaterThanMin,
  numericRequiredWithGreaterThanMin,
} from "../../Baseline/validationSchema/validationObjectBuilderFunctions";
import { interventionFieldWithAtLeastMin, interventionFieldWithMin } from "./interventionValidationObjectBuilderFunctions";

const feedRequiredSumValidation = (intl: IntlShape, fieldName: string) =>
  interventionFieldWithAtLeastMin(intl, fieldName).test(
    'feedRequiredSum',
    '',
    function (_, context) {
      const { path, createError } = this;
      const { from }: any = context;

      if (from && from[2].value) {
        const { feed, operations } = from[2].value;

        if (feed && operations) {
          const {
            compoundFeeds, compoundFeedsAdditions,
            singleIngredients, singleIngredientsAdditions,
            naturalFeed, naturalFeedAdditions,
            baselineCompoundFeedTotal,
            baselineSingleIngredientTotal,
            baselineNaturalFeedTotal,
            baselineFertilizersTotal
          } = feed;

          const { fertilizerTypes, fertilizerTypeAdditions } = operations;

          let feedSum = 0;

          const isValid = (value: any) =>
            value !== undefined && value !== null && value !== '' && !isNaN(Number(value));

          // Compound Feeds
          if (compoundFeeds && compoundFeeds.length > 0) {
            compoundFeeds.forEach((item: any) => {
              feedSum += isValid(item.kgPerAnimal_newValue)
                ? Number(item.kgPerAnimal_newValue)
                : Number(baselineCompoundFeedTotal);
            });
          } else {
            feedSum += Number(baselineCompoundFeedTotal);
          }

          // Single Ingredients
          if (singleIngredients && singleIngredients.length > 0) {
            singleIngredients.forEach((item: any) => {
              feedSum += isValid(item.kgPerAnimal_newValue)
                ? Number(item.kgPerAnimal_newValue)
                : Number(baselineSingleIngredientTotal);
            });
          } else {
            feedSum += Number(baselineSingleIngredientTotal);
          }

          // Natural Feed
          if (naturalFeed && naturalFeed.length > 0) {
            naturalFeed.forEach((item: any) => {
              feedSum += isValid(item.quantity_newValue)
                ? Number(item.quantity_newValue)
                : Number(baselineNaturalFeedTotal);
            });
          } else {
            feedSum += Number(baselineNaturalFeedTotal);
          }

          // Fertilizer Types
          if (fertilizerTypes && fertilizerTypes.length > 0) {
            fertilizerTypes.forEach((item: any) => {
              feedSum += isValid(item.amount_newValue)
                ? Number(item.amount_newValue)
                : Number(baselineFertilizersTotal);
            });
          } else {
            feedSum += Number(baselineFertilizersTotal);
          }

          // Additions
          if (compoundFeedsAdditions) {
            compoundFeedsAdditions.forEach((item: any) => {
              if (isValid(item.kgPerAnimal)) {
                feedSum += Number(item.kgPerAnimal);
              }
            });
          }

          if (singleIngredientsAdditions) {
            singleIngredientsAdditions.forEach((item: any) => {
              if (isValid(item.kgPerAnimal)) {
                feedSum += Number(item.kgPerAnimal);
              }
            });
          }

          if (naturalFeedAdditions) {
            naturalFeedAdditions.forEach((item: any) => {
              if (isValid(item.quantity)) {
                feedSum += Number(item.quantity);
              }
            });
          }

          if (fertilizerTypeAdditions) {
            fertilizerTypeAdditions.forEach((item: any) => {
              if (isValid(item.amount)) {
                feedSum += Number(item.amount);
              }
            });
          }

          if (feedSum <= 0.0) {
            return createError({
              path,
              message: intl.formatMessage({
                id: 'SUSTELL.STAGE.DAIRY.FEED.SUM_OF_FEED.GREATER_THAN_ZERO',
              }),
            });
          }

          return true;
        }

        return false;
      }

      return false;
    }
  );


const shrimpFeedItemsTestMandatory = (
  intl: IntlShape,
  minValue = 0,
) =>
  Yup.array()
    .of(
      Yup.object({
        kgPerAnimal: Yup.number()
          .transform((changed, original) =>
            original === '' ? undefined : changed
          )
          .typeError(intl.formatMessage({ id: 'VALIDATION.NUMERIC.INPUT' }))
          .required(intl.formatMessage({ id: 'VALIDATION.NUMERIC.INPUT' }))
          .moreThan(
            minValue,
            intl.formatMessage(
              { id: 'VALIDATION.NUMERIC.INPUT_GREATER_THAN' },
              { value: minValue }
            )
          ),
        feedType: Yup.string().required(
          intl.formatMessage({ id: 'BASELINE.FORM.FEED.SHRIMP.QUANTITY_ERROR' })
        ),
      }).default({})
    );

const stageOutputPrice = (intl: IntlShape) =>
  interventionFieldWithMin(intl, "price", 0)
    .test("outputsLengthTest", (_, context) => {
      const { path, createError, parent } = context;
      const { from }: any = context;

      if (from[1]) {
        const { outputs, outputAdditions } = from[1].value;
        const outputsLength = outputs?.length + outputAdditions?.length;

        if (outputsLength > 1) {
          const baselinePrice = Number(parent.baselinePrice);
          const newValue = parent.price_newValue;

          if (Number.isNaN(baselinePrice) || baselinePrice === 0) {
            if (newValue === "" || newValue === 0) {
              return createError({
                path,
                message: intl.formatMessage({ id: "VALIDATION.INTERVENTION.RESULT.FIELD.MIN_VALUE" }, { value: 0 }),
              });
            }

            return true;
          }

          return true;
        }
      }

      return true;
    });

const stageOutputAdditionPrice = (intl: IntlShape) =>
  Yup.number()
    .transform((changed, original) =>
      original === "" ? undefined : changed
    )
    .test("outputAdditionPriceTest", (value, context) => {
      const { path, createError } = context;

      if (Number.isNaN(Number(value))) {
        return createError({
          path,
          message: intl.formatMessage({ id: "VALIDATION.NUMERIC.INPUT" }),
        });
      }

      if (Number(value) <= 0) {
        return createError({
          path,
          message: intl.formatMessage({ id: "VALIDATION.NUMERIC.INPUT_GREATER_THAN" }, { value: 0 }),
        });
      }

      return true;
    }).required();

const stageFeed = (intl: IntlShape) =>
  Yup.object({
    compoundFeeds: Yup.array().of(
      Yup.object(
        {
          originalOrigin: Yup.string().when("quantity", {
            is: (value: number) =>
              value !== undefined && value !== null,
            then: () =>
              Yup.string().nullable(),
          }),
          origin: Yup.string().when("quantity", {
            is: (value: number) =>
              value !== undefined && value !== null,
            then: () =>
              Yup.string().required(),
            otherwise: () => Yup.string().nullable(),
          }),
          originalKgPerAnimal: feedRequiredSumValidation(intl, 'kgPerAnimal'),
          kgPerAnimal: feedRequiredSumValidation(intl, 'kgPerAnimal')
        }
      ).default({})
    ),
    singleIngredients: Yup.array().of(
      Yup.object(
        {
          originalOrigin: Yup.string().when("quantity", {
            is: (value: number) =>
              value !== undefined && value !== null,
            then: () =>
              Yup.string().nullable(),
          }),
          origin: Yup.string().when("quantity", {
            is: (value: number) =>
              value !== undefined && value !== null,
            then: () =>
              Yup.string().required(),
            otherwise: () => Yup.string().nullable(),
          }),
          originalKgPerAnimal: feedRequiredSumValidation(intl, 'kgPerAnimal'),
          kgPerAnimal: feedRequiredSumValidation(intl, 'kgPerAnimal')
        }
      ).default({})
    ),
    naturalFeed: Yup.array().of(
      Yup.object(
        {
          originalQuantity: feedRequiredSumValidation(intl, 'quantity'),
          originalCrudeProteinContent: interventionFieldWithMin(intl, 'crudeProteinContent', 0),
          originalOrigin: Yup.string().when("quantity", {
            is: (value: number) =>
              value !== undefined && value !== null,
            then: () =>
              Yup.string().nullable(),
          }),
          quantity: feedRequiredSumValidation(intl, 'quantity'),
          crudeProteinContent: interventionFieldWithMin(intl, 'crudeProteinContent', 0),
          origin: Yup.string().when("quantity", {
            is: (value: number) =>
              value !== undefined && value !== null,
            then: () =>
              Yup.string().required(),
            otherwise: () => Yup.string().nullable(),
          }),
        }
      ).default({})
    ),

    compoundFeedsAdditions: shrimpFeedItemsTestMandatory(intl, 0),
    singleIngredientsAdditions: Yup.array()
      .of(
        Yup.object({
          kgPerAnimal: Yup.number()
            .transform((changed, original) =>
              original === '' ? undefined : changed
            )
            .typeError(intl.formatMessage({ id: 'VALIDATION.NUMERIC.INPUT' }))
            .required(intl.formatMessage({ id: 'VALIDATION.NUMERIC.INPUT' }))
            .moreThan(
              0,
              intl.formatMessage(
                { id: 'VALIDATION.NUMERIC.INPUT_GREATER_THAN' },
                { value: 0 }
              )
            ),
          feedType: Yup.string().required(
            intl.formatMessage({ id: 'BASELINE.FORM.FEED.SHRIMP.QUANTITY_ERROR' })
          ),
          origin: Yup.string().required(
            intl.formatMessage({ id: 'VALIDATION.FIELD.ORIGIN' })
          ),
        }).default({})
      ),
    naturalFeedAdditions: Yup.array().of(
      Yup.object({
        origin: Yup.string().required(
          intl.formatMessage({ id: 'VALIDATION.FIELD.ORIGIN' })
        ),
        quantity: Yup.number()
          .transform((changed, original) =>
            original === "" ? undefined : changed
          )
          .when("type", {
            is: (value: string) =>
              value !== undefined && value !== "",
            then: Yup.number()
              .transform((changed, original) =>
                original === "" ? undefined : changed
              )
              .typeError(
                intl.formatMessage({ id: "VALIDATION.NUMERIC.INPUT" })
              )
              .min(
                0,
                intl.formatMessage(
                  { id: "VALIDATION.FIELD.MIN_VALUE" },
                  { value: 0 }
                )
              )
              .required(
                intl.formatMessage({ id: "VALIDATION.NUMERIC.INPUT" })
              ),
          }),
        crudeProteinContent: Yup.number()
          .transform((changed, original) =>
            original === "" ? undefined : changed
          )
          .typeError(
            intl.formatMessage({ id: "VALIDATION.NUMERIC.INPUT" })
          )
          .max(
            100,
            intl.formatMessage(
              { id: "VALIDATION.FIELD.MAX_VALUE" },
              { value: 100 }
            )
          ),
      }).default({})
    ),
  }).default({});

const stageDataPartShrimp = (intl: IntlShape) =>
  Yup.object({
    stages: Yup.array().of(
      Yup.object({
        stageData: Yup.object({
          input: Yup.object({
            originalEmptyPeriod: interventionFieldWithMin(intl, 'emptyPeriod', 0),
            originalTotalPondArea: interventionFieldWithMin(intl, 'totalPondArea', 0),
            originalTotalWeightAtStartDate: interventionFieldWithMin(intl, 'totalWeightAtStartDate', 0),
            originalTotalWeightAtEndDate: interventionFieldWithMin(intl, 'totalWeightAtEndDate', 0),
            originalPriceAtStartDate: interventionFieldWithMin(intl, 'priceAtStartDate', 0),
            originalPriceAtEndDate: interventionFieldWithMin(intl, 'priceAtEndDate', 0),
            emptyPeriod: interventionFieldWithMin(intl, 'emptyPeriod', 0),
            totalPondArea: interventionFieldWithMin(intl, 'totalPondArea', 0),
            totalWeightAtStartDate: interventionFieldWithMin(intl, 'totalWeightAtStartDate', 0),
            totalWeightAtEndDate: interventionFieldWithMin(intl, 'totalWeightAtEndDate', 0),
            priceAtStartDate: interventionFieldWithMin(intl, 'priceAtStartDate', 0),
            priceAtEndDate: interventionFieldWithMin(intl, 'priceAtEndDate', 0),

            internalSources: Yup.array().of(
              Yup.object({
                originalTotalWeight: interventionFieldWithMin(intl, 'totalWeight', 0),
                originalDistanceTraveled: interventionFieldWithMin(intl, 'distanceTraveled', 0),
                totalWeight: interventionFieldWithMin(intl, 'totalWeight', 0),
                distanceTraveled: interventionFieldWithMin(intl, 'distanceTraveled', 0),
                transportMode: Yup.string().when("totalWeight", {
                  is: (value: number) =>
                    value !== undefined && value !== null,
                  then: () =>
                    Yup.string().required(),
                  otherwise: () => Yup.string().nullable(),
                }),
              }).default({}),
            ),

            externalSources: Yup.array().of(
              Yup.object({
                originalTotalWeight: interventionFieldWithMin(intl, 'totalWeight', 0),
                originalDistanceTraveled: interventionFieldWithMin(intl, 'distanceTraveled', 0),
                totalWeight: interventionFieldWithMin(intl, 'totalWeight', 0),
                distanceTraveled: interventionFieldWithMin(intl, 'distanceTraveled', 0),
                transportMode: Yup.string().when("totalWeight", {
                  is: (value: number) =>
                    value !== undefined && value !== null,
                  then: () =>
                    Yup.string().required(),
                  otherwise: () => Yup.string().nullable(),
                }),
              }).default({}),
            ),
          }).default({}),
          feed: stageFeed(intl),
          operations: Yup.object({
            originalIceAmount: interventionFieldWithMin(intl, 'iceAmount', 0),
            iceAmount: interventionFieldWithMin(intl, 'iceAmount', 0),
            fertilizerTypes: Yup.array().of(
              Yup.object({
                originalAmount: feedRequiredSumValidation(intl, 'amount'),
                amount: feedRequiredSumValidation(intl, 'amount'),
              }).default({}),
            ),
            materialTypes: Yup.array().of(
              Yup.object({
                originalAmount: interventionFieldWithMin(intl, 'amount', 0),
                originalDistanceTraveled: interventionFieldWithMin(intl, 'distanceTraveled', 0),
                originalLifetimeOfMaterial: interventionFieldWithMin(intl, 'lifetimeOfMaterial', 0),
                amount: interventionFieldWithMin(intl, 'amount', 0),
                distanceTraveled: interventionFieldWithMin(intl, 'distanceTraveled', 0),
                lifetimeOfMaterial: interventionFieldWithMin(intl, 'lifetimeOfMaterial', 0),
              }).default({}),
            ),
            chemicalTypes: Yup.array().of(
              Yup.object({
                originalAmount: interventionFieldWithMin(intl, 'amount', 0),
                amount: interventionFieldWithMin(intl, 'amount', 0),
              }).default({}),
            ),
            fertilizerTypeAdditions: Yup.array().of(
              Yup.object({
                type: Yup.string(),
                amount: Yup.number().when('type', {
                  is: (val: string) => val,
                  then: () => numericRequiredWithGreaterThanMin(intl, 0),
                  otherwise: () => numericOptionalWithGreaterThanMin(intl, 0),
                }),
              }).default({})
            ),
            materialTypeAdditions: Yup.array().of(
              Yup.object({
                type: Yup.string(),
                amount: Yup.number().when('type', {
                  is: (val: string) => val,
                  then: () => numericRequiredWithGreaterThanMin(intl, 0),
                  otherwise: () => numericOptionalWithGreaterThanMin(intl, 0),
                }),
                lifetimeOfMaterial: Yup.number().when('type', {
                  is: (val: string) => val,
                  then: () => numericRequiredWithGreaterThanMin(intl, 0),
                  otherwise: () => numericOptionalWithGreaterThanMin(intl, 0),
                }),
              }).default({})
            ),
            chemicalTypeAdditions: Yup.array().of(
              Yup.object({
                type: Yup.string(),
                amount: Yup.number().when('type', {
                  is: (val: string) => val,
                  then: () => numericRequiredWithGreaterThanMin(intl, 0),
                  otherwise: () => numericOptionalWithGreaterThanMin(intl, 0),
                }),
              }).default({})
            ),
            resourceUse: Yup.object({
              originalElectricityUse: interventionFieldWithMin(intl, 'electricityUse', 0),
              originalGasUse: interventionFieldWithMin(intl, 'gasUse', 0),
              originalNitrogenConcentrationIn: interventionFieldWithMin(intl, 'nitrogenConcentrationIn', 0),
              originalNitrogenConcentrationOut: interventionFieldWithMin(intl, 'nitrogenConcentrationOut', 0),
              originalWaterEnteringPondIn: interventionFieldWithMin(intl, 'waterEnteringPondIn', 0),
              originalWaterEnteringPondOut: interventionFieldWithMin(intl, 'waterEnteringPondOut', 0),
              originalWaterUse: interventionFieldWithMin(intl, 'waterUse', 0),
              electricityUse: interventionFieldWithMin(intl, 'electricityUse', 0),
              gasUse: interventionFieldWithMin(intl, 'gasUse', 0),
              nitrogenConcentrationIn: interventionFieldWithMin(intl, 'nitrogenConcentrationIn', 0),
              nitrogenConcentrationOut: interventionFieldWithMin(intl, 'nitrogenConcentrationOut', 0),
              waterEnteringPondIn: interventionFieldWithMin(intl, 'waterEnteringPondIn', 0),
              waterEnteringPondOut: interventionFieldWithMin(intl, 'waterEnteringPondOut', 0),
              waterUse: interventionFieldWithMin(intl, 'waterUse', 0),
              selfGeneratedRenewables: Yup.array().of(
                Yup.object({
                  originalValue: interventionFieldWithMin(intl, 'value', 0),
                  value: interventionFieldWithMin(intl, 'value', 0),
                }).default({})
              ),
              energyTypes: Yup.array().of(
                Yup.object({
                  originalValue: interventionFieldWithMin(intl, 'value', 0),
                  value: interventionFieldWithMin(intl, 'value', 0),
                }).default({})
              ),
              selfGeneratedRenewablesAdditions: Yup.array().of(
                Yup.object({
                  type: Yup.string().required(),
                  value: Yup.number().when('type', {
                    is: (val: string) => val,
                    then: () => numericRequiredWithGreaterThanMin(intl, 0),
                    otherwise: () => numericOptionalWithGreaterThanMin(intl, 0),
                  }),
                }).default({})
              ),
              energyTypesAdditions: Yup.array().of(
                Yup.object({
                  type: Yup.string().required(),
                  value: Yup.number().when('type', {
                    is: (val: string) => val,
                    then: () => numericRequiredWithGreaterThanMin(intl, 0),
                    otherwise: () => numericOptionalWithGreaterThanMin(intl, 0),
                  }),
                }).default({})
              )
            }).default({})
          }).default({}),
          output: Yup.object({
            outputs: Yup.array().of(
              Yup.object({
                originalPrice: stageOutputPrice(intl),
                originalTotalWeight: interventionFieldWithMin(intl, 'totalWeight', 0),
                originalProteinContent: interventionFieldWithMin(intl, 'proteinContent', 0),
                price: stageOutputPrice(intl),
                totalWeight: interventionFieldWithMin(intl, 'totalWeight', 0),
                proteinContent: interventionFieldWithMin(intl, 'proteinContent', 0),
              }).default({})
            ),
            outputAdditions: Yup.array().of(
              Yup.object({
                name: Yup.string()
                  .required(intl.formatMessage({ id: 'VALIDATION.NAME.REQUIRED' }))
                  .min(
                    3,
                    intl.formatMessage({ id: 'VALIDATION.FIELD.MIN_LENGTH' }, { count: 3 })
                  ),
                price: stageOutputAdditionPrice(intl),
                totalWeight: Yup.number().when('name', {
                  is: (val: string) => val,
                  then: () => numericRequiredWithGreaterThanMin(intl, 0),
                  otherwise: () => numericOptionalWithGreaterThanMin(intl, 0),
                }),
                soldExternally: Yup.string().required(
                  intl.formatMessage({ id: 'VALIDATION.FIELD.REQUIRED' })
                ),
              }).default({})
            ).default({}),
          }).default({})
        }).default({})
      }).default({})
    ).required().min(1, 'At least one stage data is needed.'),
  }).default({});

const assembleValidationSchemaSustellShrimp = (
  baseSchema: any,
  confObj: { intl: IntlShape; userUOM?: any }
) => {
  const infoObject = baseSchema;
  const combinedSchema = Yup.object({ info: infoObject })
    .concat(
      Yup.object({
        resourceUse: interventionValidationResourseUsagePartV2(confObj),
      })
    )
    .concat(stageDataPartShrimp(confObj.intl));

  return combinedSchema;
};

export default assembleValidationSchemaSustellShrimp;
